/** @format */

import React, { useState, useEffect } from "react";
import LabelInput from "../../../shared/components/labelInput";
import DriverNameFields from "../../../shared/components/driverNameFields";
import { useFormContext } from "react-hook-form";
import {
  fetchEmp,
  fetchEmpBusiness,
  fetchMartialStatus,
  fetchOccupation,
  fetchRating,
  fetchRelation,
  fetchTitlesService,
} from "../../../shared/services/simpleListItemsService";
import TqAddDriverMapper from "../mappers/tqAddDriverMapper";
import TqAddDriverClaim from "./tqAddDriverClaim";
import NewDatePicker from "../../../shared/components/newDatePicker";
import { generateDLN } from "../../../shared/utils/generateDLN";
import MonthYearField from "../../../shared/components/monthYearField";
import RadioGroup from "../../../shared/components/RadioGroup";
import FieldStack from "../../../shared/components/FieldStack";
import FieldDescription from "../../../shared/components/FieldDescription";
import ModalWrapper from "../../../shared/components/ModalWrapper";
import Drawer from "../../../shared/components/Drawer";
import InstructionBadge from "../../../shared/components/InstructionBadge";
import NumberedInstructionStep from "../../../shared/components/NumberedInstructionStep";
import { FiMenu } from "react-icons/fi";
import Button from "../../../shared/components/Button";

const TqAddDriverModal = ({
  onDriverSaved,
  isModalOpen,
  setIsModalOpen,
  localClaimFields,
  setLocalClaimFields,
}) => {
  const {
    register,
    formState: { errors, isValid },
    watch,
    setValue,
    control,
    handleSubmit,
    trigger,
    reset,
  } = useFormContext();

  const [titles, setTitles] = useState([]);
  const [occupation, setOccupation] = useState([]);
  const [martial, setMartial] = useState([]);
  const [employeeStatus, setEmployeeStatus] = useState([]);
  const [relation, setRelation] = useState([]);
  const [employeeBusiness, setEmployeeBusiness] = useState([]);
  const [rating, setRating] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState("");
  const [showClaimError, setShowClaimError] = useState(false);

  const dob = watch("addDob_Date");
  const lastName = watch("addDriver_lastName");
  const gender = watch("addDriverTitle.gender");
  const additionalClaimAnswer = watch("AdditionalClaim");
  const hasMissingClaims =
    additionalClaimAnswer === "Yes" && localClaimFields.length === 0;
  const canSave = isValid && !hasMissingClaims;

  const prioritizedOccupations = ["Taxi Driver", "Taxi Operating", "Chauffeur"];

  const prioritizeOccupations = (array) => {
    const priorityItems = [];
    const otherItems = [];

    array.forEach((item) => {
      if (prioritizedOccupations.includes(item.listItemDescription)) {
        priorityItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    priorityItems.sort((a, b) => {
      return (
        prioritizedOccupations.indexOf(a.listItemDescription) -
        prioritizedOccupations.indexOf(b.listItemDescription)
      );
    });

    return [...priorityItems, ...otherItems];
  };

  const convertArrayFormat2 = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }

    const prioritizedArray = prioritizeOccupations(array);

    return prioritizedArray.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  const convertArrayFormat = (array) => {
    return array.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
      gender: item.listItemExtraInfoValue === "M" ? "Male" : "Female",
    }));
  };

  const sortTitles = (titles) => {
    return titles.sort((a, b) => {
      const aDescription = a.listItemDescription;
      const bDescription = b.listItemDescription;

      if (
        (aDescription === "Mr" || aDescription === "Mrs") &&
        !(bDescription === "Mr" || bDescription === "Mrs")
      ) {
        return -1;
      } else if (
        !(aDescription === "Mr" || aDescription === "Mrs") &&
        (bDescription === "Mr" || bDescription === "Mrs")
      ) {
        return 1;
      } else if (
        aDescription.startsWith("M") &&
        !bDescription.startsWith("M")
      ) {
        return -1;
      } else if (
        !aDescription.startsWith("M") &&
        bDescription.startsWith("M")
      ) {
        return 1;
      }
      return 0;
    });
  };

  useEffect(() => {
    if (dob && lastName && gender) {
      const newDLN = generateDLN(lastName, dob, gender);
      setValue("addlicense", newDLN);
    }
  }, [dob, lastName, gender, setValue]);

  useEffect(() => {
    if (isModalOpen) setSaveError("");
  }, [isModalOpen]);

  useEffect(() => {
    if (!hasMissingClaims) setShowClaimError(false);
  }, [hasMissingClaims]);

  // Fetch data effects
  useEffect(() => {
    const fetchAndSetTitles = async () => {
      try {
        setIsLoading(true);
        const titles = await fetchTitlesService();
        const sortedTitles = sortTitles(titles);
        setTitles(sortedTitles);
      } catch (error) {
        console.error("Error updating titles:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetTitles();
  }, []);

  useEffect(() => {
    fetchOccupation().then((occupations) => setOccupation(occupations));
    fetchMartialStatus().then((statuses) => setMartial(statuses));
    fetchEmp().then((statuses) => setEmployeeStatus(statuses));
    fetchEmpBusiness().then((statuses) => setEmployeeBusiness(statuses));
    fetchRelation().then((statuses) => setRelation(statuses));
    fetchRating().then((statuses) => setRating(statuses));
  }, []);

  useEffect(() => {
    if (watch("occupation")?.value === "D34") {
      setValue("addDriverBusinessOccupation", { value: "703", label: "" });
    }
  }, [watch("occupation")]);

  const handleClaimsUpdate = (updatedClaims) => {
    setLocalClaimFields(updatedClaims);
    if (updatedClaims.length > 0) setShowClaimError(false);
  };

  const onSubmit = handleSubmit(
    (data) => {
      if (data.AdditionalClaim === "Yes" && localClaimFields.length === 0) {
        setShowClaimError(true);
        return;
      }

      try {
        const driverData = {
          addDriverTitle: data.addDriverTitle,
          firstName: data.addDriver_firstName,
          lastName: data.addDriver_lastName,
          addDob_Date: data.addDob_Date,
          addlicense: data.addlicense,
          addlicenseUser: data.addlicenseUser,
          rating: data.rating,
          occupation: data.occupation,
          addDriverempStatus: data.addDriverempStatus,
          addDriverBusinessOccupation: data.addDriverBusinessOccupation,
          addDriverpartTimeOcc: data.addDriverpartTimeOcc,
          addDriverpartTimeBusinessOcc: data.addDriverpartTimeBusinessOcc,
          addDriverPartTimeEmpStatus: data.addDriverPartTimeEmpStatus,
          parttime: data.parttime,
          residecy: data.residecy,
          residencyYears: data.residencyYears,
          martialStatus: data.martialStatus,
          AddDriverbatch_Month: data.AddDriverbatch_Month,
          AddDriverbatch_Year: data.AddDriverbatch_Year,
          relation: data.relation,
          addvehicleUse: data.addvehicleUse,
          index: watch("driverIndex"),
          additionalDriverClaims: localClaimFields,
        };

        TqAddDriverMapper.set(driverData);
        onDriverSaved();
        setSaveError("");
        setIsModalOpen(false);
        reset();
      } catch (error) {
        console.error("Error saving additional driver:", error);
        setSaveError(
          "We couldn’t save this driver. Please check the details and try again.",
        );
      }
    },
    () => {
      if (hasMissingClaims) setShowClaimError(true);
    },
  );

  return (
    <>
      {isModalOpen && (
        <div>
          <ModalWrapper
            heading={
              watch("driverIndex") !== null
                ? "Edit Driver"
                : "Add Additional Driver"
            }
            size="large"
            buttonText="Save Details"
            buttonVariant={canSave ? "main" : "disabled"}
            onClose={() => setIsModalOpen(false)}
            onButtonClick={onSubmit}
          >
            <FieldStack className="tw-text-sm">
              {saveError && (
                <p
                  className="tw-m-0 tw-border-0 tw-border-l-4 tw-border-solid tw-border-l-dis-error tw-bg-dis-error-bg tw-p-3 tw-text-sm tw-text-dis-error"
                  role="alert"
                >
                  {saveError}
                </p>
              )}
              <p className="tw-m-0 tw-rounded-xl tw-bg-dis-sky-tint tw-px-4 tw-py-2.5 tw-text-dis-small tw-leading-[1.45] tw-text-dis-navy">
                You can only add drivers who hold a valid Taxi badge.
              </p>
              <LabelInput
                id="addDriverTitle"
                label="Title"
                isLoading={isLoading}
                type="select"
                placeholder="e.g Mrs"
                options={convertArrayFormat(titles)}
                register={register("addDriverTitle", {
                  required: "The field is Required",
                })}
                errors={errors.addDriverTitle}
                watch={watch("addDriverTitle")}
                control={control}
                trigger={trigger}
              />
              <DriverNameFields
                {...{
                  register,
                  errors,
                  watch,
                  trigger,
                  prefix: "addDriver_",
                }}
              />
              <NewDatePicker
                name="addDob_Date"
                label="Date of birth"
                errors={errors.addDob_Date}
                setValue={setValue}
                control={control}
                rules={{
                  required: "Date is required",
                }}
                watch={watch}
                trigger={trigger}
                maxDate={new Date()}
              />
              <FieldDescription
                label="Driving licence number"
                description={`We require your Driving Licence Number ("DLN") to provide this to the DVLA to confirm your licence status, entitlement and restriction information and endorsement/conviction data.`}
              />
              <div className="tw-flex tw-items-start tw-gap-2.5 max-[425px]:tw-flex-col max-[425px]:tw-gap-4">
                <LabelInput
                  id="addlicense"
                  type="text"
                  placeholder="xxxxxxxxxxx"
                  disabled={true}
                  maxLength={11}
                  register={register("addlicense", {
                    required: "The field is Required",
                    pattern: {
                      value: /^[a-zA-Z0-9]{11}$/,
                      message: "Invalid format (xxxxxxxxxxx)",
                    },
                  })}
                  errors={errors.addlicense}
                  trigger={trigger}
                  watch={watch("addlicense")}
                  className="tw-min-w-0 tw-flex-[1.75]"
                  classNamelic="licFieldAuto"
                />
                <LabelInput
                  id="addlicenseUser"
                  type="text"
                  placeholder="xxxxx"
                  maxLength={5}
                  register={register("addlicenseUser", {
                    required: "The field is Required",
                    pattern: {
                      value: /^[a-zA-Z0-9]{5}$/,
                      message: "Invalid format (xxxxx)",
                    },
                  })}
                  errors={errors.addlicenseUser}
                  trigger={trigger}
                  watch={watch("addlicenseUser")}
                  className="tw-min-w-0 tw-flex-1"
                  classNamelic="licFieldAuto"
                />

                <Button
                  onClick={() => setIsOpen(true)}
                  size="small"
                  className="tw-shrink-0"
                  type="button"
                >
                  Need help?
                </Button>
              </div>

              <Drawer
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title="Your driving licence number"
                zIndex={1400}
              >
                <div className="tw-space-y-5">
                  <div className="tw-space-y-4 tw-text-dis-base tw-leading-[1.5] tw-text-dis-text-2">
                    <p className="tw-m-0 tw-font-bold tw-text-dis-navy">
                      You can only add or change your driving licence number on Uber.
                    </p>
                    <p className="tw-m-0">
                      We pull it through from your Uber account, so it must be correct
                      there first. It is the 16 characters in section 5 of your
                      photocard driving licence.
                    </p>
                  </div>

                  <ol className="tw-m-0 tw-list-none tw-space-y-4 tw-p-0">
                    <NumberedInstructionStep number="1">
                      Open <strong className="tw-font-bold tw-text-dis-navy">the Uber Driver app</strong>.
                      On a computer, sign in at{" "}
                      <a href="https://drivers.uber.com" target="_blank" rel="noreferrer" className="tw-font-bold tw-text-dis-navy">
                        drivers.uber.com
                      </a>.
                    </NumberedInstructionStep>
                    <NumberedInstructionStep number="2">
                      Tap <InstructionBadge icon={FiMenu}>Menu</InstructionBadge> (or your profile picture) at the top left.
                    </NumberedInstructionStep>
                    <NumberedInstructionStep number="3">
                      Tap <InstructionBadge>Account</InstructionBadge>, then{" "}
                      <InstructionBadge>Documents</InstructionBadge>.
                    </NumberedInstructionStep>
                    <NumberedInstructionStep number="4">
                      Select <InstructionBadge>Driving licence</InstructionBadge> and tap{" "}
                      <InstructionBadge>Take photo</InstructionBadge>. Upload the front, and the back if Uber asks.
                    </NumberedInstructionStep>
                    <NumberedInstructionStep number="5">
                      Wait for Uber to approve it. Uber usually reviews documents within 1 to 5 days.
                    </NumberedInstructionStep>
                    <NumberedInstructionStep number="6">
                      Come back and select <strong className="tw-font-bold tw-text-dis-navy">Get a new quote</strong>.
                      We will pull your updated licence number through from Uber.
                    </NumberedInstructionStep>
                  </ol>

                  <p className="tw-m-0 tw-rounded-xl tw-bg-dis-sky-tint tw-p-3.5 tw-text-dis-small tw-leading-[1.55] tw-text-dis-text-2">
                    The licence number on Uber must match the DVLA licence. Use a clear, colour photo of the original card showing all 4 corners. Uber may also ask for a DVLA check code.
                  </p>

                  <a
                    href="https://help.uber.com/en-GB/driving-and-delivering/article/7---documents---faq?nodeId=f0da3098-a9a0-46c5-bfcb-7b1e8e9c266a"
                    target="_blank"
                    rel="noreferrer"
                    className="tw-inline-block tw-font-bold tw-text-dis-deep-blue tw-underline tw-underline-offset-2 hover:tw-text-dis-navy"
                  >
                    Full guide: update your licence on Uber
                  </a>
                </div>
              </Drawer>

              <LabelInput
                id="rating"
                label="Uber Pro rating"
                type="select"
                placeholder="Select rating"
                control={control}
                options={convertArrayFormat(rating)}
                register={register("rating", {
                  required: "The field is Required",
                })}
                errors={errors.rating}
                watch={watch("rating")}
                trigger={trigger}
              />

              <LabelInput
                id="relation"
                label="What is your relationship to the proposer?"
                type="select"
                placeholder="Select relationship"
                control={control}
                options={convertArrayFormat(relation)}
                register={register("relation", {
                  required: "The field is Required",
                })}
                errors={errors.relation}
                watch={watch("relation")}
                trigger={trigger}
              />

              <RadioGroup
                name="addvehicleUse"
                mainLabel="In addition to working Private Hire for Uber, does the additional driver work Public Hire or as a Hackney Carriage?"
                register={register}
                trigger={trigger}
                errors={errors.addvehicleUse}
                yesValue={17}
                noValue={15}
              />

              <LabelInput
                id="occupation"
                label="Occupation"
                type="select"
                options={convertArrayFormat2(occupation)}
                control={control}
                placeholder="Select occupation"
                register={register("occupation", { required: true })}
                errors={errors.occupation}
                watch={watch("occupation")}
                trigger={trigger}
                isSearchable={true}
              />
              <LabelInput
                id="addDriverBusinessOccupation"
                label="Business occupation"
                type="select"
                options={convertArrayFormat(employeeBusiness)}
                control={control}
                placeholder="Select business occupation"
                register={register("addDriverBusinessOccupation", {
                  required: true,
                })}
                errors={errors.addDriverBusinessOccupation}
                watch={watch("addDriverBusinessOccupation")}
                trigger={trigger}
                isSearchable={true}
              />

              <LabelInput
                id="addDriverempStatus"
                label="Employment status"
                type="select"
                options={convertArrayFormat(employeeStatus)}
                control={control}
                placeholder="Select status"
                register={register("addDriverempStatus", {
                  required: true,
                })}
                errors={errors.addDriverempStatus}
                watch={watch("addDriverempStatus")}
                trigger={trigger}
              />

              <RadioGroup
                name="parttime"
                mainLabel="Do you have part-time occupation?"
                register={register}
                trigger={trigger}
                errors={errors.parttime}
              />

              {watch("parttime") === "Yes" && (
                <>
                  <LabelInput
                    id="addDriverpartTimeOcc"
                    label="Part time occupation"
                    type="select"
                    options={convertArrayFormat2(occupation)}
                    control={control}
                    placeholder="Select part time occupation"
                    register={register("addDriverpartTimeOcc", {
                      required: true,
                    })}
                    errors={errors.addDriverpartTimeOcc}
                    watch={watch("addDriverpartTimeOcc")}
                    trigger={trigger}
                    isSearchable={true}
                  />

                  <LabelInput
                    id="addDriverpartTimeBusinessOcc"
                    label="Part time business type"
                    type="select"
                    options={convertArrayFormat(employeeBusiness)}
                    control={control}
                    placeholder="Select part time business type"
                    register={register("addDriverpartTimeBusinessOcc", {
                      required: true,
                    })}
                    errors={errors.addDriverpartTimeBusinessOcc}
                    watch={watch("addDriverpartTimeBusinessOcc")}
                    trigger={trigger}
                    isSearchable={true}
                  />
                  <LabelInput
                    id="addDriverPartTimeEmpStatus"
                    label="Part time employee status "
                    type="select"
                    options={convertArrayFormat(employeeStatus)}
                    control={control}
                    placeholder="Select part time employee status"
                    register={register("addDriverPartTimeEmpStatus", {
                      required: true,
                    })}
                    errors={errors.addDriverPartTimeEmpStatus}
                    watch={watch("addDriverPartTimeEmpStatus")}
                    trigger={trigger}
                  />
                </>
              )}

              <RadioGroup
                name="residecy"
                mainLabel="Have You been resident in the UK since birth?"
                register={register}
                trigger={trigger}
                errors={errors.residecy}
              />

              {watch("residecy") === "No" && (
                <LabelInput
                  id="residencyYears"
                  label="How many years have you been resident in the UK?"
                  type="number"
                  placeholder="Enter years"
                  pattern="[0-9]*"
                  register={register("residencyYears", {
                    required: "This field is required",
                    pattern: {
                      value: /^\d{1,2}$/,
                      message: "The maximum number of digits is 2",
                    },
                  })}
                  errors={errors.residencyYears}
                  watch={watch("residencyYears")}
                  trigger={trigger}
                />
              )}

              <LabelInput
                id="martialStatus"
                label="Marital status"
                type="select"
                options={convertArrayFormat(martial)}
                control={control}
                placeholder="Select martial status"
                register={register("martialStatus", {
                  required: true,
                })}
                errors={errors.martialStatus}
                watch={watch("martialStatus")}
                trigger={trigger}
              />
              <MonthYearField
                label="When did you obtain your Private Hire License or Taxi Badge?"
                control={control}
                monthID="AddDriverbatch_Month"
                yearID="AddDriverbatch_Year"
                errors={{
                  AddDriverbatch_Month: errors.AddDriverbatch_Month,
                  AddDriverbatch_Year: errors.AddDriverbatch_Year,
                }}
                trigger={trigger}
                watch={watch}
                register={register}
                setValue={setValue}
              />
              <TqAddDriverClaim
                onClaimsUpdate={handleClaimsUpdate}
                localClaimFields={localClaimFields}
                setLocalClaimFields={setLocalClaimFields}
              />
              {showClaimError && hasMissingClaims && (
                <p
                  className="tw-m-0 tw-border-0 tw-border-l-4 tw-border-solid tw-border-l-dis-error tw-bg-dis-error-bg tw-p-3 tw-text-sm tw-text-dis-error"
                  role="alert"
                >
                  Please add at least one claim or select No to continue.
                </p>
              )}
            </FieldStack>
          </ModalWrapper>
        </div>
      )}
    </>
  );
};

export default TqAddDriverModal;
